// Admin CRUD do Hub de Agentes (P11) + filas de aprovação (P12).
// Endpoints sob `/api/admin/agents/` exigem role ADMIN ou CURATOR.
// Endpoints /api/agents/publication-requests/ e /api/agents/quota/requests/
// são compartilhados com o learner mas as actions approve/reject são admin-only.

import apiService from './api.service';
import { AdminAgent, AdminAgentFilters, AdminAgentForm } from '@/types/adminAgent';
import {
  AdminUserQuota,
  AgentPublicationRequest,
  AgentPublicationRequestDetail,
  AgentQuotaIncreaseRequest,
  ConversationFullResponse,
  UpdateUserQuotaPayload,
  UsageDashboard,
  UserQuotaChangeLog,
} from '@/types/agents';

type Paginated<T> = T[] | { results: T[] };

function unwrapList<T>(data: Paginated<T>): T[] {
  return Array.isArray(data) ? data : data.results ?? [];
}

function buildQuery(filters: AdminAgentFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

class AdminAgentsService {
  async list(filters: AdminAgentFilters = {}): Promise<AdminAgent[]> {
    const response = await apiService.get<Paginated<AdminAgent>>(
      `/admin/agents/${buildQuery(filters)}`,
    );
    return unwrapList(response.data);
  }

  async get(id: string): Promise<AdminAgent> {
    const response = await apiService.get<AdminAgent>(`/admin/agents/${id}/`);
    return response.data;
  }

  async create(payload: Partial<AdminAgentForm>): Promise<AdminAgent> {
    const response = await apiService.post<AdminAgent>('/admin/agents/', payload);
    return response.data;
  }

  async update(id: string, payload: Partial<AdminAgentForm>): Promise<AdminAgent> {
    const response = await apiService.patch<AdminAgent>(`/admin/agents/${id}/`, payload);
    return response.data;
  }

  async remove(id: string): Promise<void> {
    await apiService.delete(`/admin/agents/${id}/`);
  }

  async publish(id: string): Promise<AdminAgent> {
    const response = await apiService.post<AdminAgent>(`/admin/agents/${id}/publish/`);
    return response.data;
  }

  async unpublish(id: string): Promise<AdminAgent> {
    const response = await apiService.post<AdminAgent>(`/admin/agents/${id}/unpublish/`);
    return response.data;
  }

  async activate(id: string): Promise<AdminAgent> {
    const response = await apiService.post<AdminAgent>(`/admin/agents/${id}/activate/`);
    return response.data;
  }

  async deactivate(id: string): Promise<AdminAgent> {
    const response = await apiService.post<AdminAgent>(`/admin/agents/${id}/deactivate/`);
    return response.data;
  }

  // --- P12: fila de aprovações de publicação ---

  async listPublicationRequests(filters: { status?: string } = {}): Promise<AgentPublicationRequest[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const response = await apiService.get<Paginated<AgentPublicationRequest>>(
      `/agents/publication-requests/${qs ? `?${qs}` : ''}`,
    );
    return unwrapList(response.data);
  }

  async getPublicationRequest(id: string): Promise<AgentPublicationRequestDetail> {
    const response = await apiService.get<AgentPublicationRequestDetail>(
      `/agents/publication-requests/${id}/`,
    );
    return response.data;
  }

  async approvePublication(id: string, notes: string): Promise<AgentPublicationRequest> {
    const response = await apiService.post<AgentPublicationRequest>(
      `/agents/publication-requests/${id}/approve/`,
      { notes },
    );
    return response.data;
  }

  async rejectPublication(id: string, notes: string): Promise<AgentPublicationRequest> {
    const response = await apiService.post<AgentPublicationRequest>(
      `/agents/publication-requests/${id}/reject/`,
      { notes },
    );
    return response.data;
  }

  // --- P12: fila de pedidos de cota ---

  async listQuotaIncreaseRequests(filters: { status?: string } = {}): Promise<AgentQuotaIncreaseRequest[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    });
    const qs = params.toString();
    const response = await apiService.get<Paginated<AgentQuotaIncreaseRequest>>(
      `/agents/quota/requests/${qs ? `?${qs}` : ''}`,
    );
    return unwrapList(response.data);
  }

  async approveQuotaIncrease(
    id: string,
    newValue: number,
    notes: string,
  ): Promise<AgentQuotaIncreaseRequest> {
    const response = await apiService.post<AgentQuotaIncreaseRequest>(
      `/agents/quota/requests/${id}/approve/`,
      { new_value: newValue, notes },
    );
    return response.data;
  }

  async rejectQuotaIncrease(id: string, notes: string): Promise<AgentQuotaIncreaseRequest> {
    const response = await apiService.post<AgentQuotaIncreaseRequest>(
      `/agents/quota/requests/${id}/reject/`,
      { notes },
    );
    return response.data;
  }

  // --- P13: dashboard de uso por agente ---

  async getUsage(agentId: string): Promise<UsageDashboard> {
    const response = await apiService.get<UsageDashboard>(`/admin/agents/${agentId}/usage/`);
    return response.data;
  }

  async getConversationFull(agentId: string, convId: string): Promise<ConversationFullResponse> {
    const response = await apiService.get<ConversationFullResponse>(
      `/admin/agents/${agentId}/conversations/${convId}/full/`,
    );
    return response.data;
  }

  // --- P13: cota de user (endpoints admin via /api/admin/users/<id>/agent-quota/) ---

  async getUserQuota(userId: string): Promise<AdminUserQuota> {
    const response = await apiService.get<AdminUserQuota>(
      `/admin/users/${userId}/agent-quota/`,
    );
    return response.data;
  }

  async updateUserQuota(userId: string, payload: UpdateUserQuotaPayload): Promise<AdminUserQuota> {
    const response = await apiService.patch<AdminUserQuota>(
      `/admin/users/${userId}/agent-quota/`,
      payload,
    );
    return response.data;
  }

  async resetUserQuotaCounters(userId: string): Promise<AdminUserQuota> {
    const response = await apiService.post<AdminUserQuota>(
      `/admin/users/${userId}/agent-quota/reset/`,
    );
    return response.data;
  }

  async getUserQuotaAudit(userId: string): Promise<UserQuotaChangeLog[]> {
    const response = await apiService.get<UserQuotaChangeLog[]>(
      `/admin/users/${userId}/agent-quota/audit/`,
    );
    return Array.isArray(response.data) ? response.data : [];
  }
}

export default new AdminAgentsService();
